import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { useFormState } from 'react-use-form-state';
import { Input, Label, Form, FormGroup, FormFeedback } from 'reactstrap';
import { Button } from '@shoflo/polymer';

import BannerTemplateChooser from 'components/BannerLayerSourceForm/BannerTemplateChooser/BannerTemplateChooser';
import BannerTemplateListPropType from 'components/BannerTemplateListPropType';
import LayerSourcePropType from 'components/LayerSourcePropType';
import getFieldValidator from './fieldValidatorFactory';
import FieldTypes from './FieldTypes';
import { BannerStyles, BASIC_BANNER } from 'constants/LayerPositions';
import BannerLayerSourceFormTextInput from './TextInput';
import BannerLayerSourceFormHexColorInput from './HexColorInput';
import { StyledCancelButton } from './BannerLayerSourceForm.styled';
const BannerLayerSourceForm = ({ templates, banner, onSubmit, onCancel }) => {
  const bannerLayerSourceTitleRef = useRef();

  useEffect(() => {
    bannerLayerSourceTitleRef.current.focus();
  }, []);

  const source = banner && banner.source;

  // if !!source === true we are editing source
  const [isUnEdited, setIsUnEdited] = useState(!!source);

  const initialTemplate = source
    ? templates.find(({ _id }) => _id === source.value.templateId)
    : templates[0];

  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const statefulFields = {
    title: source ? source.title : ''
  };

  // eslint-disable-next-line no-unused-vars
  const [autoLeave, setAutoLeave] = useState(
    (source && source.value.autoLeave) || false
  );

  selectedTemplate.fields.forEach(f => {
    statefulFields[f.label] =
      source && source.value.templateVars[f.key]
        ? source.value.templateVars[f.key]
        : '';
  });

  const [formState, { text }] = useFormState();

  useEffect(() => {
    if (isUnEdited) {
      Object.keys(statefulFields).forEach(key => {
        formState.setField(key, statefulFields[key]);
      });
      setIsUnEdited(false);
    }
  }, [isUnEdited, statefulFields, formState]);

  return (
    <div>
      <Form
        data-test="bannerLayerSourceForm"
        onSubmit={e => {
          e.preventDefault();
          setIsSubmitting(true);

          const templateVars = {};

          selectedTemplate.fields.forEach(f => {
            templateVars[f.key] = formState.values[f.label];
          });

          const layerSource = {
            source: {
              title: formState.values.title,
              value: {
                templateId: selectedTemplate._id,
                templateVars,
                autoLeave,
                transitionStyle: ''
              }
            },
            layer: {
              title: BASIC_BANNER,
              style: BannerStyles[BASIC_BANNER]
            }
          };

          onSubmit(layerSource);
        }}
      >
        <BannerTemplateChooser
          options={templates}
          value={selectedTemplate}
          formValues={formState.values}
          onChange={t => {
            setSelectedTemplate(t);
          }}
        />

        <FormGroup>
          <Label
            id="bannerLayerSourceTitleLabel"
            htmlFor="bannerLayerSourceTitle"
          >
            Banner title
          </Label>
          <Input
            data-testid="bannerLayerSourceTitleInput"
            id="bannerLayerSourceTitle"
            innerRef={bannerLayerSourceTitleRef}
            aria-labelledby="bannerLayerSourceTitleLabel"
            placeholder="e.g. My Banner"
            {...text({
              name: 'title',
              validate: t => {
                return t.length >= 1;
              },
              validateOnBlur: false,
              touchOnChange: true
            })}
            invalid={formState.touched.title && !formState.validity.title}
            required
          />
          <FormFeedback>Uh oh! A banner title is required.</FormFeedback>
        </FormGroup>

        {/* Render dynamically the template fields */}
        {selectedTemplate.fields.map(field => {
          const Component = fieldTypeMap[field.type];

          const inputProps = {
            invalid:
              formState.touched[field.label] &&
              !formState.validity[field.label],
            ...text({
              name: field.label,
              validate: getFieldValidator(field),
              validateOnBlur: false,
              touchOnChange: true
            })
          };
          return (
            <Component
              key={`dyn-field-li-${field.label}`}
              field={field}
              formState={formState}
              inputProps={inputProps}
            />
          );
        })}

        <div className="float-end">
          <Button
            data-testid="submitButton"
            disabled={!formIsValid(formState, selectedTemplate) || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>{' '}
          <StyledCancelButton
            data-test="cancelButton"
            color="light"
            variant="ghost"
            onClick={e => {
              e.preventDefault();
              onCancel();
            }}
          >
            Cancel
          </StyledCancelButton>
        </div>
      </Form>
    </div>
  );
};

const formIsValid = (formState, selectedTemplate) => {
  const fields = ['title', ...selectedTemplate.fields.map(f => f.label)];

  return _.all(fields, field => {
    return formState.values[field] && formState.validity[field];
  });
};

const fieldTypeMap = {
  [FieldTypes.Text]: BannerLayerSourceFormTextInput,
  [FieldTypes.HexColor]: BannerLayerSourceFormHexColorInput
};

BannerLayerSourceForm.propTypes = {
  templates: BannerTemplateListPropType,
  banner: LayerSourcePropType,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default BannerLayerSourceForm;
